import { ReactElement } from 'react';

import { getConfig } from '../utils/configUtils';
import { HorizontalButtonPairView } from '../components/DRViewUtils';
import PinCodeEntry from '../components/PinCodeEntry';
import { cancelColor, confirmColor, confirmBorderColor } from '../constants/Styles';
import CornerGripperLogo from '../components/CornerGripperLogo';

import './ForgotPinCodeScreen.scss';

export interface ForgotPinCodeScreenProps {
  code: Map<number, number | null>;
  currentStep: number;
  handleCancel: () => void;
  handleContinue: () => void;
  setCode: (code: Map<number, number | null>) => void;
  setCurrentStep: (step: number) => void;
  tapToReload: () => void;
  selectedOption: string;
  setSelectedOption: (option: string) => void;
  selectedReason: string;
  setSelectedReason: (reason: string) => void;
  recipient: string;
  setRecipient: (recipient: string) => void;
  inputError: string;
  setInputError: (error: string) => void;
  forgotPinCodeClicked: boolean;
  tasksToUnload: string[];
  pickupLocation: string;
}

interface ScreenStep {
  title: string;
  subtitle?: string;
  buttons: ReactElement;
}

export default function ForgotPinCodeScreen({
  code,
  currentStep,
  handleCancel,
  handleContinue,
  setCode,
  setCurrentStep,
  tapToReload,
  selectedOption,
  setSelectedOption,
  selectedReason,
  setSelectedReason,
  recipient,
  setRecipient,
  inputError,
  setInputError,
  forgotPinCodeClicked,
  tasksToUnload,
  pickupLocation,
}: ForgotPinCodeScreenProps) {
  const supportNumber = getConfig().site?.supportNumber;

  const handleGoBack = () => {
    if (currentStep === 0) {
      handleCancel();
    } else {
      if (tasksToUnload.length === 1 && (currentStep === 4 || currentStep === 5) && forgotPinCodeClicked) {
        setCurrentStep(2);
      } else {
        setCurrentStep(currentStep - 1);
      }
    }
  };

  const getButtonSettings = () => {
    const buttonSettings = [
      {
        phrase: 'GO BACK',
        borderColor: cancelColor,
        fillColor: 'transparent',
        fontColor: cancelColor,
        action: handleGoBack,
      },
      {
        phrase: 'CONTINUE',
        fillColor: confirmColor,
        borderColor: confirmBorderColor,
        fontColor: 'white',
        action: handleContinue,
        disabled: isContinueDisabled(),
      },
    ];

    if ((forgotPinCodeClicked && currentStep === 0) || currentStep === 5) return buttonSettings;
    if (forgotPinCodeClicked && currentStep > 0) return [buttonSettings[0]];
    if ((!forgotPinCodeClicked && currentStep === 0) || (!forgotPinCodeClicked && currentStep === 5))
      return [buttonSettings[1]];
    return null;
  };

  const isContinueDisabled = () => currentStep === 5 && !recipient.trim().length;

  const getTitleClasses = () => {
    let classes = 'forgot-pin-code-screen__title';
    if (currentStep === 2) classes += ' forgot-pin-code-screen__title--tall';
    if (currentStep === 0 && forgotPinCodeClicked) classes += ' forgot-pin-code-screen__title--slim';
    return classes;
  };

  const steps: ScreenStep[] = [
    {
      title: forgotPinCodeClicked
        ? 'If you forgot the code, an administrator will need to help.'
        : "You've run out of code attempts, and an administrator will need to help.",
      subtitle: "Your administrator is likely your department's lead or supervisor.",
      buttons: <HorizontalButtonPairView buttonSettings={getButtonSettings()} />,
    },
    {
      title: 'If you are the administrator, scan your badge on the sticker below this screen',
      subtitle: `If you need help, contact Moxi support ${!!supportNumber ? '(' + supportNumber + ')' : ''}`,
      buttons: <HorizontalButtonPairView buttonSettings={getButtonSettings()} />,
    },
    {
      title: 'Enter the override code',
      buttons: <HorizontalButtonPairView buttonSettings={getButtonSettings()} />,
    },
    {
      title: 'Choose what to do with this delivery',
      buttons: <HorizontalButtonPairView buttonSettings={getButtonSettings()} />,
    },
    {
      title: 'Why was an override code necessary?',
      buttons: <HorizontalButtonPairView buttonSettings={getButtonSettings()} />,
    },
    {
      title: 'Who is assuming chain of custody?',
      buttons: <HorizontalButtonPairView buttonSettings={getButtonSettings()} />,
    },
  ];

  const handleOverrideOption = (option: string) => {
    setSelectedOption(selectedOption === option ? '' : option);
    setCurrentStep(currentStep + 1);
  };

  const handleOverrideReason = (reason: string) => {
    setSelectedReason(selectedReason === reason ? '' : reason);
    setCurrentStep(currentStep + 1);
  };

  const renderOption = (type: 'option' | 'reason', label: ReactElement, value: string) => {
    const isSelected = type === 'option' ? selectedOption === value : selectedReason === value;
    return (
      <button
        className={`options-item${isSelected ? ' options-item--selected' : ''}`}
        onClick={() => (type === 'option' ? handleOverrideOption(value) : handleOverrideReason(value))}
      >
        <div className={`option-icon${isSelected ? ' option-icon--selected' : ''}`}></div>
        {label}
      </button>
    );
  };

  const renderStepContent = () => {
    if (currentStep === 1) {
      return (
        <img
          className="content__moxi-scan"
          draggable="false"
          src={require('../assets/images/moxi-badge-reader.gif')}
          alt="Moxi badge scan"
        />
      );
    }
    if (currentStep === 2) {
      return (
        <div className="content__input">
          <PinCodeEntry code={code} isInvalid={!!inputError} setCode={setCode} setInputError={setInputError} />
          <div role="alert" className="content__input-error">
            {!!inputError ? inputError : ''}
          </div>
        </div>
      );
    }
    if (currentStep === 3) {
      return (
        <div className="content__options">
          <div className="content__options--list">
            {tasksToUnload.length > 1 && renderOption('option', <>Open All Drawers for This Delivery</>, 'open-all')}
            {renderOption(
              'option',
              <span>
                Open Drawer(s) from <span className="option--highlighted">{pickupLocation}</span>
              </span>,
              'open',
            )}
            {/* {renderOption('option', 'Return to Sender', 'return')} */}
          </div>
        </div>
      );
    }
    if (currentStep === 4) {
      return (
        <div className="content__options">
          <div className="content__options--list">
            {renderOption('reason', <>The recipient forgot their code</>, 'forgot')}
            {renderOption('reason', <>The recipient is unavailable</>, 'unavailable')}
            {renderOption('reason', <>To ensure timely patient care</>, 'timely')}
            {renderOption('reason', <>The previous user is unfamiliar with this workflow</>, 'unfamiliar')}
          </div>
        </div>
      );
    }
    if (currentStep === 5) {
      return (
        <div className="content__input">
          <input
            placeholder="Recipient"
            className="recipient-input"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
          />
        </div>
      );
    }
    return null;
  };

  return (
    <div className="forgot-pin-code-screen">
      <CornerGripperLogo tapToReload={tapToReload} />
      <div className="forgot-pin-code-screen__content">
        <h1 className={getTitleClasses()}>{steps[currentStep].title}</h1>
        {steps[currentStep].subtitle && (
          <h4 className="forgot-pin-code-screen__subtitle">{steps[currentStep].subtitle}</h4>
        )}
        {renderStepContent()}
      </div>

      <div className="forgot-pin-code-screen__footer">{steps[currentStep].buttons}</div>
    </div>
  );
}
